import { useCallback, useEffect, useRef, useState } from "react";
import {
  type BuyerMinimumRequirementReviewResponsePatchDto,
  type BuyerPublicBidDetailDto,
  useUpdateBuyerMinimumRequirementReviewMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

interface EditorState {
  verdicts: Record<string, boolean | null>;
  comments: Record<string, string>;
}

interface UseEditor {
  state: EditorState;
  setVerdict: (minimumRequirementId: string, value: boolean | null) => void;
  setComment: (minimumRequirementId: string, value: string) => void;
  isSaving: boolean;
}

export const useBuyerMinimumRequirementReviewEditor = (
  eventId: string,
  bidId: string,
  detail: BuyerPublicBidDetailDto,
): UseEditor => {
  const { trigger } = useApiError();
  const [update, { isLoading: isSaving }] = useUpdateBuyerMinimumRequirementReviewMutation();
  const [state, setState] = useState<EditorState>(() => initialState(detail));
  const pendingById = useRef<Map<string, BuyerMinimumRequirementReviewResponsePatchDto>>(new Map());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(isSaving);
  savingRef.current = isSaving;

  useEffect(() => {
    // A save invalidates the PublicBid cache tag, which refetches `detail`.
    // Don't let that server snapshot clobber edits the buyer is still making.
    if (pendingById.current.size > 0 || savingRef.current) return;
    setState(initialState(detail));
  }, [detail]);

  const flush = useCallback(async () => {
    if (pendingById.current.size === 0) return;
    const responses = Array.from(pendingById.current.values());
    pendingById.current.clear();
    try {
      await update({
        eventId,
        bidId,
        buyerMinimumRequirementReviewPatchDto: { responses },
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  }, [bidId, eventId, trigger, update]);

  const schedule = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => {
      flushTimer.current = null;
      void flush();
    }, DEBOUNCE_MS);
  }, [flush]);

  // Flush any debounced edit when the tab unmounts, so the last keystroke is not lost when the
  // buyer navigates away inside the debounce window.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(
    () => () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      void flushRef.current();
    },
    [],
  );

  const enqueueVerdict = useCallback(
    (minimumRequirementId: string, value: boolean | null) => {
      const current = pendingById.current.get(minimumRequirementId) ?? { minimumRequirementId };
      pendingById.current.set(minimumRequirementId, { ...current, buyerVerdict: value });
      schedule();
    },
    [schedule],
  );

  const enqueueComment = useCallback(
    (minimumRequirementId: string, value: string) => {
      const current = pendingById.current.get(minimumRequirementId) ?? { minimumRequirementId };
      pendingById.current.set(minimumRequirementId, { ...current, buyerComment: value });
      schedule();
    },
    [schedule],
  );

  const setVerdict = useCallback(
    (minimumRequirementId: string, value: boolean | null) => {
      setState((s) => ({ ...s, verdicts: { ...s.verdicts, [minimumRequirementId]: value } }));
      enqueueVerdict(minimumRequirementId, value);
    },
    [enqueueVerdict],
  );

  const setComment = useCallback(
    (minimumRequirementId: string, value: string) => {
      setState((s) => ({ ...s, comments: { ...s.comments, [minimumRequirementId]: value } }));
      enqueueComment(minimumRequirementId, value);
    },
    [enqueueComment],
  );

  return { state, setVerdict, setComment, isSaving };
};

const initialState = (detail: BuyerPublicBidDetailDto): EditorState => {
  const verdicts: Record<string, boolean | null> = {};
  const comments: Record<string, string> = {};
  for (const r of detail.bid.minimumRequirementResponses ?? []) {
    verdicts[r.minimumRequirementId] = r.buyerVerdict ?? null;
    comments[r.minimumRequirementId] = r.buyerComment ?? "";
  }
  return { verdicts, comments };
};
